import { create } from 'zustand';

type Language = 'en' | 'ja';

interface I18nStore {
  language: Language;
  setLanguage: (language: Language) => void;
}

const getInitialLanguage = (): Language => {
  const savedLanguage = localStorage.getItem('language') as Language;
  if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'ja')) {
    return savedLanguage;
  }
  return 'ja';
};

export const useI18n = create<I18nStore>((set) => ({
  language: getInitialLanguage(),
  setLanguage: (language) => {
    localStorage.setItem('language', language);
    set({ language });
  },
}));

export const translations = {
  en: {
    nav: {
      features: 'Features',
      pricing: 'Pricing',
      waitlist: 'Join Waitlist',
    },
    hero: {
      badge: '#1 AI UGC video generator',
      title: 'Easiest way to create\nAI videos for your app',
      subtitle: 'Generate killer short-form videos in minutes.',
      cta: 'Create your first video',
      perfect: 'Perfect for',
      stats: 'videos generated by founders',
    },
    features: {
      title: 'Everything you need to promote your app',
      subtitle: 'Powerful features to help you create engaging content',
      list: [
        {
          title: 'AI-Powered Actors',
          description: "No more unsuitable existing videos. 100% AI-generated videos."
        },
        {
          title: 'Dynamic Scenes',
          description: 'Choose from a wide range of scenes to achieve the perfect look for your videos.'
        },
        {
          title: 'Natural Voice',
          description: 'AI actors trained to speak with natural, human-like voices.'
        },
        {
          title: '2 Languages Support',
          description: "Non-English audience? No problem. Generate videos in 2 languages."
        },
        {
          title: 'AI Captions',
          description: 'Add AI-generated captions to your videos.'
        },
        {
          title: 'Perfect for Ads',
          description: 'Promote organic growth and reduce CPC for paid advertising.'
        }
      ]
    },
    priceComparison: {
      title: 'The Painful Alternative',
      subtitle: 'Save yourself the hassle of working with creators.',
      withUgcmax: {
        title: 'With Shortmaker',
        features: [
          'Generate multiple videos',
          'Get ready-to-use videos in 15 minutes',
          'Full commercial usage rights included',
          'AI actor matches your audience'
        ],
        price: '¥480',
        perVideo: '/video',
        nofees: 'No hidden fees, costs the same for each video'
      },
      traditional: {
        title: 'Traditional UGC Creators',
        features: [
          'Pay ¥16,000-56,000 per video',
          '1-2 weeks delivery time',
          "Hoping you don't get screwed over",
          'Limited usage rights'
        ],
        price: '¥16,000+',
        perVideo: '/video',
        fees: 'With hidden fees'
      },
      cta: {
        text: 'Pay 10x less than with creators',
        button: 'Create your first AI video'
      }
    },
    pricing: {
      title: 'Create UGC Videos for Less',
      subtitle: {
        average: 'The average cost of a UGC video is',
        averagePrice: '¥8,000-56,000',
        our: 'Our AI solution generates a video for less than',
        ourPrice: '¥480'
      },
      plans: {
        solo: {
          name: 'Solo',
          description: 'Start generating UGC videos with AI.',
          price: '4,640'
        },
        pro: {
          name: 'Pro',
          description: 'Perfect for growing startups.',
          price: '14,240',
          popular: 'Most Popular'
        },
        agency: {
          name: 'Agency',
          description: 'Unleash the full potential of UGC videos.',
          price: '46,240'
        },
        features: {
          videos: {
            solo: '10 videos per month',
            pro: '30 videos per month',
            agency: '100 videos per month'
          },
          actors: {
            solo: '5 custom AI actors',
            pro: '10 custom AI actors',
            agency: '20 custom AI actors'
          },
          writer: 'AI script writer',
          voiceover: {
            solo: '30 minutes of voiceover audio',
            pro: '2 hours of voiceover audio',
            agency: '4 hours of voiceover audio'
          },
          languages: '2 languages supported'
        },
        subscribe: 'Subscribe Now',
        cancel: 'You can cancel your subscription anytime.'
      }
    },
    faq: {
      title: 'Questions & Answers',
      questions: [
        {
          question: 'How can I use the videos to grow my app?',
          answer: 'You can use the videos to grow your app by sharing them on TikTok, Instagram, YouTube shorts, and other platforms.'
        },
        {
          question: 'Is the AI-generated video quality good enough for ads?',
          answer: 'Yes. Our AI generates studio-quality videos that perform well on TikTok, Instagram, and Facebook.'
        },
        {
          question: 'How much can I really save compared to traditional UGC creators?',
          answer: 'Traditional UGC costs ¥16,000-56,000 per video. With Shortmaker, you pay ¥480 per video - a 96% cost reduction.'
        },
        {
          question: 'Do I get full commercial rights to use the videos?',
          answer: 'Yes. You get 100% commercial rights to use the videos anywhere without restrictions.'
        },
        {
          question: 'Is UGCMAX free to use?',
          answer: 'Shortmaker does not offer a free tier at this time. You can purchase the Solo, Pro, or Agency plan.'
        },
        {
          question: 'How fast can I get my videos?',
          answer: 'Videos are generated in under 15 minutes, compared to 1-2 weeks with traditional UGC creators.'
        },
        {
          question: 'Can I generate actors that fit my brand?',
          answer: 'Yes, you can generate actors that fit your brand. You can precisely customize the actor\'s appearance and environment.'
        },
        {
          question: 'What languages are supported?',
          answer: 'Currently, we support English and Japanese.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'UGCMAX subscriptions are non-refundable due to high costs of generating videos. However, you can cancel anytime to stop future charges. If you experience technical issues, contact support and we\'ll help resolve them.'
        }
      ]
    },
    steps: {
      title: 'Create AI UGC Videos in 3 Steps',
      subtitle: 'Enhance your marketing with AI',
      steps: [
        {
          title: 'Generate AI Actor',
          description: 'Enter basic information to generate your AI actor.',
        },
        {
          title: 'Generate AI Hook',
          description: 'Create a 5-second hook to highlight your product or service.',
        },
        {
          title: 'Export',
          description: 'Add captions, adjust the script, and export your video.',
        },
      ],
      interface: {
        actor: {
          title: 'Actor Settings',
          gender: 'Gender',
          genderOptions: {
            female: 'Female',
            male: 'Male'
          },
          age: 'Age',
          ageOptions: {
            young: '18-24',
            middle: '25-34',
            mature: '35-44'
          },
          personality: 'Personality',
          personalityOptions: {
            energetic: 'Energetic',
            professional: 'Professional',
            friendly: 'Friendly'
          },
          voiceTone: 'Voice Tone',
          voiceToneOptions: {
            natural: 'Natural',
            enthusiastic: 'Enthusiastic',
            calm: 'Calm'
          }
        },
        hook: {
          title: 'Hook Generator',
          timeline: 'Timeline',
          scene: 'Scene',
          bgm: 'BGM'
        },
        export: {
          title: 'Export Settings',
          captions: {
            title: 'Captions',
            description: 'Add auto-generated captions'
          },
          resolution: {
            title: 'Resolution',
            description: '1080x1920 (9:16)',
            change: 'Change'
          }
        }
      }
    },
    waitlist: {
      title: 'Join the Waitlist',
      subtitle: 'Be the first to know when we launch',
      name: 'Name',
      email: 'Email',
      submit: 'Join Waitlist',
      success: 'Thanks for joining! We\'ll notify you when we launch.',
      error: 'Something went wrong. Please try again later.',
    },
    cta: {
      title: 'Save 96% on UGC videos',
      subtitle: 'Get studio-quality UGC videos for $3 each. No more waiting weeks or paying $100-350 per video to creators.',
      button: 'Start Creating Videos',
    },
    footer: {
      description: 'Create UGC videos with AI.',
      copyright: 'Copyright © 2025 - All rights reserved',
      links: {
        title: 'Links',
        support: 'Support',
      },
      legal: {
        title: 'Legal',
        terms: 'Terms and conditions',
        privacy: 'Privacy policy',
        refund: 'Refund policy',
      },
    },
    language: 'English',
    videoExamples: {
      videos: [
        {
          title: "Product Demo",
          views: "2.1M views",
          duration: "0:45"
        },
        {
          title: "How to Use",
          views: "1.8M views",
          duration: "1:20"
        },
        {
          title: "Case Study",
          views: "3.2M views",
          duration: "0:58"
        }
      ]
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'If you have any questions or requests, please feel free to contact us.',
      form: {
        name: 'Name',
        email: 'Email',
        message: 'Message',
        submit: 'Submit',
        sending: 'Sending...',
        success: 'Thank you for your inquiry. We will get back to you soon.',
        error: 'An error occurred. Please try again later.'
      }
    },
  },
  ja: {
    nav: {
      features: '機能',
      pricing: '料金',
      waitlist: '早期アクセスに参加 👀',
    },
    hero: {
      badge: '#1 AI 縦型動画ジェネレーター(予定)',
      title: 'バズるショートは\nAIで量産する時代',
      subtitle: 'テキストを入力するだけ\n完全自動で流行りのショート動画を大量生成 🔥',
      cta: '早期アクセスをゲットする 🎁',
      perfect: '対応プラットフォーム',
      stats: '本の動画が生成されました',
    },
    features: {
      title: 'マーケを成功させるのに必要な全部入り💪',
      subtitle: '魅力的なコンテンツを作り出す充実のAIツール',
      list: [
        {
          title: 'AIパワードアクター',
          description: "ブランドに合わない従来の動画素材は不要。100%AI生成のオリジナル動画を作成できます。"
        },
        {
          title: 'ダイナミックなシーン',
          description: '豊富なシーンから選んで、理想的なビジュアルを実現。'
        },
        {
          title: 'リアルな声',
          description: 'まるで人が話しているかのような自然なバーチャルインフルエンサーの声 🫢'
        },
        {
          title: '2言語対応',
          description: "英語以外の視聴者にも対応。2言語で動画を生成できます。"
        },
        {
          title: 'AI字幕',
          description: 'AIが自動生成した字幕をワンクリックで追加可能。'
        },
        {
          title: '広告にも最適',
          description: 'オーガニックな拡散を促し、有料広告のCPC削減にも貢献します。'
        }
      ]
    },
    priceComparison: {
      title: 'バズるかわからんショート動画なのに製作費用高すぎません？',
      subtitle: '',
      withUgcmax: {
        title: 'Shortmakerの場合',
        features: [
          '複数の動画を同時に生成可能',
          'わずか15分で完成動画を入手',
          '商用利用権をすべて含む',
          'ターゲットに合ったバーチャルインフルエンサーを作成'
        ],
        price: '480円',
        perVideo: '/動画',
        nofees: '追加料金なし'
      },
      traditional: {
        title: '従来のUGCクリエイター',
        features: [
          '1動画あたり16,000〜56,000円',
          '納品に1〜2週間かかる',
          '品質が安定せず、リスクがある',
          '使用権に制限あり'
        ],
        price: '16,000円〜',
        perVideo: '/動画',
        fees: '+ 追加料金'
      },
      cta: {
        text: 'クリエイター依頼の10分の1以下のコスト',
        button: '最初のAI動画を作成'
      }
    },
    pricing: {
      title: 'リーズナブルな料金プラン',
      subtitle: {
        average: 'UGC動画の一般的な制作費用は',
        averagePrice: '8,000〜56,000円',
        our: '当社のAIなら1本あたり',
        ourPrice: '480円で生成可能'
      },
      plans: {
        solo: {
          name: 'ソロ',
          description: 'まずはAI動画制作を始めたい方に最適。',
          price: '4,640'
        },
        pro: {
          name: 'プロ',
          description: '成長を目指すスタートアップにおすすめ。',
          price: '14,240',
          popular: '人気プラン'
        },
        agency: {
          name: 'エージェンシー',
          description: 'UGC動画の可能性を最大限に活かしたい企業向け。',
          price: '46,240'
        },
        features: {
          videos: {
            solo: '月10動画',
            pro: '月30動画',
            agency: '月100動画'
          },
          actors: {
            solo: 'カスタムAIアクター5体',
            pro: 'カスタムAIアクター10体',
            agency: 'カスタムAIアクター20体'
          },
          writer: 'AIスクリプトライター',
          voiceover: {
            solo: 'ボイスオーバー30分分',
            pro: 'ボイスオーバー2時間分',
            agency: 'ボイスオーバー4時間分'
          },
          languages: '2言語対応'
        },
        subscribe: '今すぐ申し込む',
        cancel: 'いつでもキャンセル可能です。'
      }
    },
    faq: {
      title: 'よくある質問',
      questions: [
        {
          question: '動画はどのようにアプリの成長に活用できますか？',
          answer: 'TikTok、Instagram、YouTubeショートなどに投稿して、多くのユーザーにリーチできます。'
        },
        {
          question: '広告に使えるほどの品質ですか？',
          answer: 'はい。TikTokやInstagram、Facebook広告などでも通用する高品質な動画を生成します。'
        },
        {
          question: '従来のUGCクリエイターと比べ、どれくらいコスト削減できますか？',
          answer: '通常16,000〜56,000円ほどかかるUGC制作を、UGCMAXなら1動画あたり480円で提供。96%のコストダウンが可能です。'
        },
        {
          question: '商用利用権は含まれていますか？',
          answer: 'はい。生成された動画はすべて商用利用可能で、制限もありません。'
        },
        {
          question: '無料で利用できますか？',
          answer: '現在は無料プランの提供はありません。ソロ、プロ、エージェンシーのいずれかのプランをご利用ください。'
        },
        {
          question: '動画はどのくらいの時間でできますか？',
          answer: '従来のクリエイターなら1〜2週間かかるところ、UGCMAXなら15分以内に完成します。'
        },
        {
          question: 'ブランドに合ったアクターを作成できますか？',
          answer: 'はい。アクターの見た目や環境を細かくカスタマイズして、ブランドに最適化可能です。'
        },
        {
          question: '対応言語は何がありますか？',
          answer: '現在は英語と日本語の2言語に対応しています。'
        },
        {
          question: '返金は可能ですか？',
          answer: '動画生成に高いコストがかかるため、サブスクリプションの返金は行っておりません。ただし、いつでもキャンセル可能なので、次回以降の請求は停止できます。技術的な問題があればサポートまでお問い合わせください。'
        }
      ]
    },
    steps: {
      title: '3ステップで動画を生成 🎥',
      subtitle: 'テンプレを選んだら、あとはいい感じにAIがやってくれます 🤖',
      steps: [
        {
          title: 'テンプレ選択 or 作成',
          description: 'Shortmakerにはすでに数字が伸びてる動画データが蓄積してるので\nフォーマットが蓄積されてるので、そこから選んでください'
        },
        {
          title: 'AIフックを生成',
          description: '5秒ほどのフックを作り、サービスや商品の魅力を一気に引き立てます。'
        },
        {
          title: '完成・エクスポート',
          description: '字幕を自動追加し、スクリプトを微調整して、すぐに動画を書き出せます。'
        }
      ],
      interface: {
        actor: {
          title: 'アクター設定',
          gender: '性別',
          genderOptions: {
            female: '女性',
            male: '男性'
          },
          age: '年齢',
          ageOptions: {
            young: '18-24歳',
            middle: '25-34歳',
            mature: '35-44歳'
          },
          personality: '個性',
          personalityOptions: {
            energetic: '活発',
            professional: 'プロフェッショナル',
            friendly: 'フレンドリー'
          },
          voiceTone: '声のトーン',
          voiceToneOptions: {
            natural: 'ナチュラル',
            enthusiastic: '元気',
            calm: '落ち着いた'
          }
        },
        hook: {
          title: 'フック生成',
          timeline: 'タイムライン',
          scene: 'シーン',
          bgm: 'BGM'
        },
        export: {
          title: 'エクスポート設定',
          captions: {
            title: '字幕',
            description: 'AIによる自動字幕を追加'
          },
          resolution: {
            title: '解像度',
            description: '1080x1920 (9:16)',
            change: '変更'
          }
        }
      }
    },
    waitlist: {
      title: 'β版ユーザー募集中 🙌',
      subtitle: 'β版に順次ご招待します！👋',
      name: 'お名前',
      email: 'メールアドレス',
      submit: '参加する',
      success: 'ご登録ありがとうございます。ローンチ時にご連絡いたします。',
      error: 'エラーが発生しました。時間をおいて再度お試しください。',
    },
    cta: {
      title: 'UGC動画制作費を最大96%削減',
      subtitle: '1本あたりわずか300円でスタジオ品質のUGC動画を制作。高額な費用や数週間の待ち時間はもう必要ありません。',
      button: '動画制作を始める',
    },
    footer: {
      description: 'AIでUGC動画を作成し、効率的にプロモーションを加速させましょう。',
      copyright: 'Copyright © 2025 - All rights reserved',
      links: {
        title: 'リンク',
        support: 'サポート',
      },
      legal: {
        title: '法的情報',
        terms: '利用規約',
        privacy: 'プライバシーポリシー',
        refund: '返金ポリシー',
      },
    },
    language: '日本語',
    videoExamples: {
      videos: [
        {
          title: "プロダクトデモ",
          views: "2.1M回視聴",
          duration: "0:45"
        },
        {
          title: "使い方ガイド",
          views: "1.8M回視聴",
          duration: "1:20"
        },
        {
          title: "事例紹介",
          views: "3.2M回視聴",
          duration: "0:58"
        }
      ]
    },
    contact: {
      title: 'お問い合わせ',
      subtitle: 'ご質問やリクエストがございましたら、お気軽にご連絡ください。',
      form: {
        name: 'お名前',
        email: 'メールアドレス',
        message: 'メッセージ',
        submit: '送信する',
        sending: '送信中...',
        success: 'お問い合わせありがとうございます。追ってご連絡いたします。',
        error: 'エラーが発生しました。時間をおいて再度お試しください。'
      }
    },
  }
};
